import { SafeError } from '@benzinga/safe-await';
import { ExtendedSubscribable, SubscribableEvent } from './subscribable';
interface SocketRequestEvent extends SubscribableEvent<'request'> {
  msg: string | ArrayBuffer | ArrayBufferView | Blob;
}

interface SocketResponseEvent<T> extends SubscribableEvent<'response'> {
  msg: T;
}

interface SocketErrorEvent extends SubscribableEvent<'error'> {
  errorEvent: SafeError;
}

interface SocketCloseEvent extends SubscribableEvent<'close'> {
  event: CloseEvent;
}

export type SubscribableSocketEvent<RESPFormat> =
  | SocketCloseEvent
  | SocketErrorEvent
  | SocketRequestEvent
  | SocketResponseEvent<RESPFormat>
  | SubscribableEvent<'open'>
  | SubscribableEvent<'closing'>;

export type SocketState = 'closed' | 'closing' | 'open' | 'opening';

export type WebSocketProps = Partial<Pick<WebSocket, 'binaryType'>>;

interface SocketFunctions {
  close: SubscribableSocket['close'];
  open: SubscribableSocket['open'];
  send: SubscribableSocket['send'];
  sendObject: SubscribableSocket['sendObject'];
}

class SubscribableSocket<RESPFormat = unknown, REQFormat = unknown> extends ExtendedSubscribable<
  SubscribableSocketEvent<RESPFormat>,
  SocketFunctions
> {
  private socket?: WebSocket;
  private pendingSocket?: WebSocket;
  private url: URL;
  private state: SocketState;
  private webSocketProps: WebSocketProps;
  private queueSend: (string | ArrayBuffer | ArrayBufferView | Blob)[] = [];

  constructor(url: URL, webSocketProps?: WebSocketProps) {
    super();
    this.url = url;
    this.webSocketProps = webSocketProps ?? {};
    this.state = 'closed';
  }

  public open = (): void => {
    if (this.socket !== undefined || this.state === 'opening') {
      return;
    }

    this.state = 'opening';
    const socket = new WebSocket(this.url.toString());
    socket.binaryType = this.webSocketProps.binaryType ?? 'blob';
    this.pendingSocket = socket;

    socket.onopen = () => {
      if (this.pendingSocket !== socket || this.state !== 'opening') {
        try {
          socket.close();
        } catch {
          console.warn('SubscribableSocket: could not close socket');
        }
        return;
      }
      this.state = 'open';
      this.socket = socket;
      this.pendingSocket = undefined;
      this.queueSend.forEach(data => this.send(data));
      this.queueSend = [];
      this.dispatch({ type: 'open' });
    };

    socket.onmessage = (event: MessageEvent) => {
      if (this.socket === socket) {
        this.dispatch({ msg: event.data, type: 'response' });
      }
    };

    socket.onerror = (event: Event) => {
      if (this.socket === socket) {
        this.dispatch({
          errorEvent: new SafeError('Scanner socket disconnected', 'socket', event),
          type: 'error',
        });
      }
    };

    socket.onclose = (event: CloseEvent) => {
      if (this.socket === socket) {
        this.socket = undefined;
        this.state = 'closed';
        this.dispatch({ event: event, type: 'close' });
        return;
      }

      // Handshake failed / cancelled while still opening. Let reconnect layer retry once.
      if (this.pendingSocket === socket) {
        this.pendingSocket = undefined;
        if (this.state === 'opening') {
          this.state = 'closed';
          this.dispatch({ event: event, type: 'close' });
        }
      }
    };
  };

  public close = (): void => {
    this.close_internal();
    this.queueSend = [];
  };

  public sendObject = <T = REQFormat>(data: T): void => {
    this.send(JSON.stringify(data));
  };

  public send = (data: string | ArrayBuffer | ArrayBufferView | Blob): void => {
    switch (this.state) {
      case 'opening':
        this.queueSend.push(data);
        break;
      case 'open':
        try {
          // WebSocket.send only accepts ArrayBuffer-backed views; callers (e.g. protobuf .finish())
          // pass ArrayBufferLike-backed Uint8Arrays which are never SharedArrayBuffer in practice.
          this.socket?.send(data as string | ArrayBuffer | ArrayBufferView<ArrayBuffer> | Blob);
        } catch (event) {
          this.dispatch({ errorEvent: new SafeError((event as Event).toString(), 'socket', event), type: 'error' });
        }
        this.dispatch({ msg: data, type: 'request' });
        break;
      case 'closed':
        console.warn('SubscribableSocket: dropped outbound message, socket is not open');
        break;
    }
  };

  public getState = (): SocketState => this.state;

  protected close_internal = (): void => {
    switch (this.state) {
      case 'open': {
        const closingSocket = this.socket;
        this.socket = undefined;
        try {
          this.state = 'closing';
          this.dispatch({ type: 'closing' });
          if (closingSocket) {
            closingSocket.onclose = (event: CloseEvent) => {
              // A newer open() may already be in flight or live. Ignore this late close
              // so we do not mark that connection closed / start a reconnect loop.
              if (this.socket !== undefined || this.state === 'opening' || this.state === 'open') {
                return;
              }
              this.state = 'closed';
              this.dispatch({ event: event, type: 'close' });
            };
            closingSocket.close();
          } else {
            this.state = 'closed';
          }
        } catch (event) {
          this.dispatch({ errorEvent: new SafeError((event as Event).toString(), 'socket', event), type: 'error' });
        }
        break;
      }
      case 'opening': {
        const pendingSocket = this.pendingSocket;
        this.pendingSocket = undefined;
        this.state = 'closed';
        this.socket = undefined;
        if (pendingSocket) {
          try {
            // Drop handlers so abort close does not look like a transport failure reconnect.
            pendingSocket.onopen = null;
            pendingSocket.onmessage = null;
            pendingSocket.onerror = null;
            pendingSocket.onclose = null;
            pendingSocket.close();
          } catch {
            console.warn('SubscribableSocket: could not close pending socket');
          }
        }
        break;
      }
    }
  };

  protected onSubscribe(): SocketFunctions {
    return {
      close: this.close,
      open: this.open,
      send: this.send,
      sendObject: this.sendObject,
    };
  }

  protected onZeroSubscriptions(): void {
    this.close();
  }
}

export default SubscribableSocket;
